import { randomId } from '@aha-app/mvc';
import { Z_INCREMENT } from '../zManager';
import { xmlComponentRegistry } from './xmlComponentRegistry';
import { extractXmlDocFromString } from './xmlParserUtils';
import { processSpecialItem } from './xmlSpecialItemHandlers';

/**
 * Represents a whiteboard object created from XML
 */
export interface WhiteboardObject {
  id: string;
  type: string;
  container?: string;
  top?: number;
  left?: number;
  width?: number | string;
  height?: number | string;
  layoutDirection?: string;
  gap?: number | string;
  padding?: number | string;
  z: number;
  [key: string]: any;
}

/**
 * Converts XML doc string to whiteboard objects
 */
export function convertXmlToWhiteboardObjects(
  xmlString: string
): WhiteboardObject[] | null {
  try {
    const xmlDoc = extractXmlDocFromString(xmlString);

    const whiteboardObjects: WhiteboardObject[] = [];
    const objectsById: Record<string, WhiteboardObject> = {};
    const childrenByParentId: Record<string, string[]> = {};
    const currentZ = 0;

    if (xmlDoc.documentElement) {
      // Recurse through nodes in XML doc
      processNodeAndChildren(
        xmlDoc.documentElement,
        undefined,
        whiteboardObjects,
        objectsById,
        childrenByParentId,
        currentZ
      );

      // Root element starts at 0,0 TODO start diagram position relative to current viewport
      if (whiteboardObjects.length > 0) {
        whiteboardObjects[0].top = 0;
        whiteboardObjects[0].left = 0;
      }

      calculatePositions(whiteboardObjects, objectsById, childrenByParentId);

      return whiteboardObjects;
    } else {
      throw new Error('XML document has no root element');
    }
  } catch (error) {
    console.error('Error parsing XML string:', error);
    return null;
  }
}

/**
 * Processes an XML node and its children recursively, converting them to whiteboard objects
 */
function processNodeAndChildren(
  node: Element,
  container: string | undefined,
  whiteboardObjects: WhiteboardObject[],
  objectsById: Record<string, WhiteboardObject>,
  childrenByParentId: Record<string, string[]>,
  currentZ: number
): number {
  if (node.nodeType === Node.COMMENT_NODE) {
    return currentZ;
  }

  // Handle nested items that end up in itemsData for the parent wireframe component
  if (container && objectsById[container]) {
    const parentObj = objectsById[container];
    // Get parent type from the registry
    const parentWireframeName = parentObj.wireframeName;
    const parentKey = Object.keys(xmlComponentRegistry).find(
      key => xmlComponentRegistry[key].wireframeName === parentWireframeName
    );
    const parentConfig = parentKey
      ? xmlComponentRegistry[parentKey]
      : undefined;

    if (processSpecialItem(node, parentObj, parentConfig?.specialItemTags)) {
      // Process any children of this special item if present
      Array.from(node.children).forEach(child => {
        if (child.nodeType !== Node.COMMENT_NODE) {
          currentZ = processNodeAndChildren(
            child,
            container,
            whiteboardObjects,
            objectsById,
            childrenByParentId,
            currentZ
          );
        }
      });

      return currentZ; // Skip normal processing for this node
    }
  }

  // Normal processing for regular nodes
  const obj = composeComponentProps(node, container);
  if (obj) {
    obj.z = currentZ;
    currentZ += Z_INCREMENT;

    whiteboardObjects.push(obj);
    objectsById[obj.id] = obj;

    if (container) {
      if (!childrenByParentId[container]) {
        childrenByParentId[container] = [];
      }
      childrenByParentId[container].push(obj.id);
    }

    // Process all child elements
    Array.from(node.children).forEach(child => {
      if (child.nodeType !== Node.COMMENT_NODE) {
        currentZ = processNodeAndChildren(
          child,
          obj.id,
          whiteboardObjects,
          objectsById,
          childrenByParentId,
          currentZ
        );
      }
    });
  }

  return currentZ;
}

/**
 * Calculate positions for all objects based on their layout
 */
function calculatePositions(
  objects: WhiteboardObject[],
  objectsById: Record<string, WhiteboardObject>,
  childrenByParentId: Record<string, string[]>
): void {
  Object.keys(childrenByParentId).forEach(container => {
    const parent = objectsById[container];
    const children = childrenByParentId[container].map(id => objectsById[id]);
    const isVertical = parent.layoutDirection === 'vertical';

    const paddingValue =
      typeof parent.padding === 'string'
        ? parseInt((parent.padding.match(/\d+/) || ['0'])[0], 10)
        : typeof parent.padding === 'number'
        ? parent.padding
        : 0;

    let nextTop = paddingValue;
    let nextLeft = paddingValue;

    // Position each child
    children.forEach(child => {
      // Set position relative to parent
      child.top = (parent?.top ?? 0) + nextTop;
      child.left = (parent?.left ?? 0) + nextLeft;

      // Calculate width and height if not explicitly set, ensuring they're numeric
      const childWidth =
        typeof child.width === 'number'
          ? child.width
          : child.width !== undefined && child.width !== 'auto'
          ? parseFloat(String(child.width))
          : 100;

      const childHeight =
        typeof child.height === 'number'
          ? child.height
          : child.height !== undefined && child.height !== 'auto'
          ? parseFloat(String(child.height))
          : 40;

      // Gap setting is currently hardcoded to 0 to match original behavior
      const gap = 0;

      // Update next position based on layout direction
      if (isVertical) {
        nextTop += childHeight + gap;
      } else {
        nextLeft += childWidth + gap;
      }
    });

    // Update parent dimensions if auto
    if (parent.width === 'auto' || parent.height === 'auto') {
      if (isVertical) {
        parent.height = nextTop;
        // Find widest child to determine parent width
        const maxChildWidth = Math.max(
          ...children.map(c => {
            if (typeof c.width === 'number') return c.width;
            return c.width !== undefined && c.width !== 'auto'
              ? parseFloat(String(c.width))
              : 100;
          })
        );
        parent.width = maxChildWidth + paddingValue * 2;
      } else {
        parent.width = nextLeft;
        // Find tallest child to determine parent height
        const maxChildHeight = Math.max(
          ...children.map(c => {
            if (typeof c.height === 'number') return c.height;
            return c.height !== undefined && c.height !== 'auto'
              ? parseFloat(String(c.height))
              : 40;
          })
        );
        parent.height = maxChildHeight + paddingValue * 2;
      }
    }
  });
}

/**
 * Creates a component's properties based on its XML node and registry configuration
 */
export function composeComponentProps(
  node: Element,
  container?: string
): WhiteboardObject | null {
  const tagName = node.tagName.toLowerCase();
  const config = xmlComponentRegistry[tagName];

  // Create base object
  const baseObj: WhiteboardObject = {
    id: randomId(),
    type: config?.type ?? 'wireframe',
    wrapText: false,
    z: 0,
  };

  if (container) {
    baseObj.container = container;
  }

  // Process attributes and labels
  const props = processAttributes(node);
  const labelProps = processLabelAttributes(node);

  // Apply defaults from registry
  const defaultProps = config?.defaultProps || {};

  let result = {
    ...baseObj,
    ...defaultProps,
    ...props,
    ...labelProps,
  };

  // Handle text content for text elements
  if (node.tagName.toLowerCase() === 'text' && node.textContent) {
    result.textContent = node.textContent;
  }

  // Apply component-specific transformations
  if (config) {
    if (config.type === 'wireframe' && config.wireframeName) {
      result.wireframeName = config.wireframeName;
    } else if (config.type === 'shape' && config.shapeName) {
      result.shapeName = config.shapeName;
    }

    // Use custom attribute processor if provided
    if (config.processAttributes) {
      result = config.processAttributes(result);
    }

    // Process placeholder if needed
    if (config.placeholderOptions) {
      result = processComponentPlaceholder(
        result,
        config.placeholderOptions.defaultValue || '',
        {
          wireframeName: result.wireframeName,
          defaultIconName: config.placeholderOptions.defaultIconName,
          defaultIconPosition: config.placeholderOptions.defaultIconPosition,
          alwaysAddIcon: config.placeholderOptions.alwaysAddIcon,
        }
      );
    }

    // Apply layout properties for stack components
    if (['vstack', 'hstack'].includes(tagName)) {
      if (node.getAttribute('verticalSize')) {
        result.layoutSizeVertical = node.getAttribute('verticalSize');
        delete result.height;
      }

      if (node.getAttribute('horizontalSize')) {
        result.layoutSizeHorizontal = node.getAttribute('horizontalSize');
        delete result.width;
      }

      if (node.getAttribute('verticalAlignment')) {
        result.layoutAlignVertical = node.getAttribute('verticalAlignment');
      }

      if (node.getAttribute('horizontalAlignment')) {
        result.layoutAlignHorizontal = node.getAttribute('horizontalAlignment');
      }
    }
  } else {
    // Invalid node, do nothing. This should not happen, but the model may sometimes
    // hallucinate out of spec nodes.
    return null;
  }

  return result;
}

/**
 * Processes XML attributes and converts them to appropriate types
 */
function processAttributes(node: Element): Record<string, any> {
  const result: Record<string, any> = {};

  Array.from(node.attributes).forEach(attr => {
    if (['type', 'label', 'labelPosition'].includes(attr.name)) return;

    // Convert attribute values to appropriate types
    let value: any = attr.value;

    // Type conversion
    if (value === 'true' || value === 'false') {
      value = value === 'true';
    } else if (value.endsWith('px') && /^-?\d+(\.\d+)?px$/.test(value)) {
      value = parseFloat(value.slice(0, -2));
    }

    // Special handling for certain attributes
    if (
      ['width', 'height'].includes(attr.name) &&
      typeof value === 'string' &&
      !value.includes('px')
    ) {
      // Handle relative width/height if needed
      result[attr.name] = value;
    } else if (attr.name === 'placeholder') {
      result.placeholder = { value };
    } else if (
      [
        'textColor',
        'inputBorderColor',
        'breadcrumbTextColor',
        'paginationTextColor',
        'selectedItemTextColor',
        'fill',
      ].includes(attr.name)
    ) {
      result[attr.name] = value;
    } else {
      result[attr.name] = value;
    }
  });

  return result;
}

/**
 * Processes label attributes from an Element
 */
function processLabelAttributes(node: Element): Record<string, any> {
  const labelValue = node.getAttribute('label');
  const labelPosition = node.getAttribute('labelPosition');

  if (!labelValue) return {};

  const result: Record<string, any> = { label: { value: labelValue } };
  if (labelPosition) {
    result.label.position = labelPosition;
  }

  return result;
}

/**
 * Processes icon attributes to create the appropriate icon object structure
 */
function processIconAttributes(
  iconName?: string,
  iconPosition: string = 'left'
): any {
  if (!iconName) return {};

  if (iconPosition === 'right') {
    return { iconRight: { prefix: 'fas', iconName } };
  } else {
    return { iconLeft: { prefix: 'fas', iconName } };
  }
}

/**
 * Processes a component with icon properties and adds the appropriate icon to its placeholder.
 */
function processComponentPlaceholder(
  baseObj: any,
  defaultPlaceholderValue: string = '',
  options: {
    wireframeName: string;
    defaultIconName?: string;
    defaultIconPosition?: string;
    alwaysAddIcon?: boolean;
  }
): any {
  // Create a clean object without icon-specific properties
  const {
    iconName: baseIconName,
    iconPosition: baseIconPosition,
    ...cleanBaseObj
  } = baseObj;
  const iconName = baseIconName ?? options.defaultIconName;
  const iconPosition =
    baseIconPosition ?? options.defaultIconPosition ?? 'left';

  // Setup the placeholder
  let placeholder: { value?: string } = {};
  if (cleanBaseObj.placeholder) {
    // The model gives us a string value for placeholder
    placeholder.value = cleanBaseObj.placeholder.value;
  } else if (defaultPlaceholderValue) {
    placeholder.value = defaultPlaceholderValue;
  }

  // Add icon if present or if alwaysAddIcon is true
  if (iconName || options.alwaysAddIcon) {
    const iconData = processIconAttributes(iconName, iconPosition);
    placeholder = { ...placeholder, ...iconData };
  }

  // Return the component with the configured placeholder and wireframeName
  return {
    ...cleanBaseObj,
    placeholder,
    wireframeName: options.wireframeName,
  };
}
